import React, { useState } from 'react';
import { format } from 'date-fns';
import PropTypes from 'prop-types';

import BarChart from '../../library/bar-chart/bar-chart';
import DateToggle from '../date-toggle/date-toggle';

import styles from './popular-times.scss';

export function byHourOfDay(a, b) {
  const aAmPm = a?.hourOfDay?.slice(-2);
  const bAmPm = b?.hourOfDay?.slice(-2);
  const aHour = parseFloat(a?.hourOfDay?.replace(/:.*$/, ''));
  const bHour = parseFloat(b?.hourOfDay?.replace(/:.*$/, ''));
  return (
    (aHour !== 12 && aAmPm === 'PM' ? aHour + 12 : aHour) -
    (bHour !== 12 && bAmPm === 'PM' ? bHour + 12 : bHour)
  );
}

function PopularTimes(props) {
  const { dates, onSelectDate, selectedDate, showGraph = true, times } = props;
  const selectedDayOfWeek = format(selectedDate || '', 'ddd');
  const selectedDayTimeData = times
    ?.find?.(time => time.dayOfWeek === selectedDayOfWeek)
    ?.peakHourData?.sort?.(byHourOfDay);
  const [selectedHourOfDay, setSelectedHourOfDay] = useState(
    format(new Date(), 'h:00 A')
  );
  const [date] = (Array.isArray(dates) ? dates : []).filter(
    date => date === selectedDate
  );
  let selectedTimeIdx = 0;
  let selectedTime = null;
  let values = [];
  let lastAvailableTimeInt = 0;
  let labels = [];
  let ariaLabels;

  if (selectedDayTimeData) {
    selectedTimeIdx = selectedDayTimeData.findIndex(
      t => t?.hourOfDay === selectedHourOfDay
    );
    selectedTime =
      selectedTimeIdx > -1
        ? selectedDayTimeData[selectedTimeIdx]
        : selectedDayTimeData[0];
    values = selectedDayTimeData.map(time => time?.peakTimesData?.mean);
    ariaLabels = selectedDayTimeData.map(
      label => label.hourOfDay + ' ' + label.peakTimesData?.message
    );
    const lastAvailableTime =
      selectedDayTimeData[selectedDayTimeData.length - 1];
    lastAvailableTimeInt =
      parseFloat(lastAvailableTime?.hourOfDay?.substring?.(0, 2)) + 1;
    labels = selectedDayTimeData.map((time, idx) => {
      let label = time?.hourOfDay?.replace?.(/:.*$/, '');
      if (idx === 0) label += 'a';
      if (label === '12') label += 'p';
      return label;
    });

    labels.push(`${lastAvailableTimeInt}p`);
  }

  const maxValue = Math.max(...values) * 1.1;

  function selectTime({ idx }) {
    const time = selectedDayTimeData[idx] || selectedDayTimeData[0];
    setSelectedHourOfDay(time?.hourOfDay);
  }

  if (!times || !dates) {
    return null;
  }

  return (
    <div className="popular-times">
      <div className={styles['head']}>
        <strong className={styles['title']}>Popular Times:</strong>
        <DateToggle
          dates={dates}
          defaultDate={date}
          onSelectDate={onSelectDate}
          selectedDate={selectedDate}
        />
      </div>
      {!window.isPreRendering && selectedTime && showGraph && (
        <>
          <BarChart
            ariaLabels={ariaLabels}
            initialValueIdx={selectedTimeIdx}
            labels={labels}
            maxValue={maxValue}
            onBarClick={selectTime}
            selectedBarMessage={
              <div className={styles['time-message']}>
                <strong>
                  {selectedHourOfDay?.replace?.(':00', '')?.toLowerCase?.()}
                </strong>{' '}
                {selectedTime?.peakTimesData?.message}
              </div>
            }
            values={values}
          />

          <div className={styles['disclaimer']}>
            *Based on previous business data collected on this date.
          </div>
        </>
      )}
    </div>
  );
}

PopularTimes.propTypes = {
  dates: PropTypes.array,
  onSelectDate: PropTypes.func,
  selectedDate: PropTypes.string,
  showGraph: PropTypes.bool,
  times: PropTypes.array
};

export default PopularTimes;
